import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';

export interface AcaoMenu {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
  /** Tooltip — útil pra explicar por que está desabilitado. */
  title?: string;
  /** Ação destrutiva (ex.: cancelar/excluir) — texto/hover em rose. */
  danger?: boolean;
}

interface Props {
  acoes: AcaoMenu[];
  /** Rótulo acessível do botão (default "Ações"). */
  rotulo?: string;
}

/**
 * Menu de ações "kebab" (⋯) reutilizável pra linhas de tabela / cards. As ações
 * vêm como lista (ícone + nome). O painel é renderizado via portal com position
 * fixed — necessário porque tabelas ficam dentro de wrappers com `overflow-x-auto`
 * (que recortam dropdowns absolutos). Fecha em clique-fora, Esc, scroll e resize;
 * abre pra cima quando não há espaço abaixo.
 */
const MenuAcoes: React.FC<Props> = ({ acoes, rotulo = 'Ações' }) => {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const abrir = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const altEstimada = acoes.length * 36 + 8;
    const abreParaCima = r.bottom + altEstimada > window.innerHeight && r.top > altEstimada;
    setPos({
      top: abreParaCima ? r.top - altEstimada - 4 : r.bottom + 4,
      right: Math.max(8, window.innerWidth - r.right),
    });
    setAberto(true);
  };

  useEffect(() => {
    if (!aberto) return;
    const fechar = () => setAberto(false);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setAberto(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
    };
  }, [aberto]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => (aberto ? setAberto(false) : abrir())}
        aria-haspopup="menu"
        aria-expanded={aberto}
        title={rotulo}
        className="flex h-8 w-8 items-center justify-center rounded border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
      >
        <EllipsisVerticalIcon className="h-4 w-4" />
      </button>
      {aberto && pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: 'fixed', top: pos.top, right: pos.right }}
            className="z-50 min-w-[180px] overflow-hidden rounded-md border border-zinc-200 bg-white py-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
          >
            {acoes.map((a) => (
              <button
                key={a.key}
                type="button"
                role="menuitem"
                disabled={a.disabled}
                title={a.title}
                onClick={() => {
                  setAberto(false);
                  a.onClick();
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs disabled:cursor-not-allowed disabled:opacity-40 ${
                  a.danger
                    ? 'text-rose-600 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950'
                    : 'text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800'
                }`}
              >
                <a.icon className="h-4 w-4 flex-shrink-0" />
                <span>{a.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default MenuAcoes;
